import { InputAdornment, TextField, Typography } from "@material-ui/core";
import { useStyles } from "../../../../../Styles/styles";
import CheckboxWithLabel from "../../../../CheckboxWithLabel";
import styles from "../../SizeAndWeightStyles.module.css";

import NumberField from "../../NumberFields/NumberFields";
import { useEffect } from "react";
import { useMutation } from "@apollo/client";
import { ADD_OR_UPDATE_AXLE_GROUP_IN_SIZE_AND_WEIGHT } from "../../../Mutations/AddOrUpdateAxleGroupInSizeAndWeight";
import { ADD_OR_UPDATE_AXLE_IN_AXLE_GROUP } from "../../../Mutations/AddOrUpdateAxleInAxleGroup";
import AxleGroup from "../AxleGroup/AxleGroup";
import CollapsingHeader from "../../CollapsingHeader/CollapsingHeader";
import DimensionNumberField from "../../DimensionNumberField";
import { REMOVE_AXLE_GROUP_FROM_SIZE_AND_WEIGHT } from "../../../Mutations/RemoveAxleGroupFromSizeAndWeight";
import addButton from "../../../../../Assets/addButton.png";

import Required from "../../Required/Required";

const autoCreateLockKey = sizeAndWeightId =>
  `sw:auto-create-axle-group:${sizeAndWeightId}`;

const getAutoCreateLock = sizeAndWeightId => {
  try {
    return window.sessionStorage.getItem(autoCreateLockKey(sizeAndWeightId));
  } catch (e) {
    return null;
  }
};

const setAutoCreateLock = (sizeAndWeightId, value) => {
  try {
    window.sessionStorage.setItem(autoCreateLockKey(sizeAndWeightId), value);
  } catch (e) {
    // no-op if storage is unavailable
  }
};

const clearAutoCreateLock = sizeAndWeightId => {
  try {
    window.sessionStorage.removeItem(autoCreateLockKey(sizeAndWeightId));
  } catch (e) {
    // no-op if storage is unavailable
  }
};

const AxleGroupWeightsSection = ({
  mostOverWeightAxleGroup = {},
  sizeAndWeightId = "",
  userId = "",
  axleGroups = [],
  weightLimits = {},
  disabled = true,
  handleChange = () => console.error("No handleChange function provided"),
  userRoles = [],
  dispatch = () => console.error("No dispatch function provided"),
  missingFields = [],
  AxleGroupComponent = AxleGroup,
  updateGrossWeight,
  updateCalculatedWeights,
  nightMode,
  locked,
  ...props
}) => {
  const classes = useStyles();
  const {
    roadLimitWeight,
    registeredWeight,
    platformWeigh,
    steerAxleTireSize,
    platformOverUnder,
    roadLimitOverUnder,
    grossWeight,
    steerAxleLimit,
  } = weightLimits || {};

  const [addAxleGroup] = useMutation(
    ADD_OR_UPDATE_AXLE_GROUP_IN_SIZE_AND_WEIGHT,
    {
      context: { serializationKey: "incidentQueue" },
      variables: {
        input: {
          sizeAndWeightId: sizeAndWeightId,
          originatorId: userId,
        },
      },
      onCompleted: data => {
        setAutoCreateLock(sizeAndWeightId, "done");
        dispatch({
          type: "ADD_AXLE_GROUP",
          property: "axleGroups",
          payload: {
            value: data.addOrUpdateAxleGroupInSizeAndWeight.axleGroup,
          },
        });
      },
      onError: () => {
        clearAutoCreateLock(sizeAndWeightId);
      },
    },
  );

  const [updateAxleGroup] = useMutation(
    ADD_OR_UPDATE_AXLE_GROUP_IN_SIZE_AND_WEIGHT,
    {
      context: { serializationKey: "incidentQueue" },
    },
  );

  const [updateAxleInAxleGroupTopLevel] = useMutation(
    ADD_OR_UPDATE_AXLE_IN_AXLE_GROUP,
    {
      context: { serializationKey: "incidentQueue" },
    },
  );

  const [removeAxleGroup] = useMutation(
    REMOVE_AXLE_GROUP_FROM_SIZE_AND_WEIGHT,
    {
      context: { serializationKey: "incidentQueue" },
      onCompleted: () => {
        updateGrossWeight();
      },
    },
  );

  const handlePlatformWeighChange = val => {
    const message = `Changing to platform weigh will remove all axle ${val ? "weights" : "group weights"}. Are you sure you want to continue?`;
    if (window.confirm(message)) {
      handleChange(val, "UPDATE_PROPERTY", { property: "platformWeigh" });
      updateGrossWeight(0);
      if (val) {
        // Remove all axle weights
        axleGroups.forEach(group => {
          group?.axles?.forEach(axle => {
            if (axle.leftAxleWeight !== 0 || axle.rightAxleWeight !== 0) {
              updateAxleInAxleGroupTopLevel({
                variables: {
                  input: {
                    axleGroupId: group.id,
                    axleId: axle.id,
                    leftAxleWeight: 0,
                    rightAxleWeight: 0,
                    totalAxleWeight: 0,
                    originatorId: userId,
                  },
                },
              });
              dispatch({
                type: "UPDATE_AXLE",
                payload: {
                  axleGroupId: group.id,
                  axleId: axle.id,
                  value: 0,
                  property: "leftAxleWeight",
                },
              });
              dispatch({
                type: "UPDATE_AXLE",
                payload: {
                  axleGroupId: group.id,
                  axleId: axle.id,
                  value: 0,
                  property: "rightAxleWeight",
                },
              });
            }
          });
        });
      } else {
        axleGroups.forEach(group => {
          if (
            group.weight !== 0 &&
            group.weight !== null &&
            group.weight !== undefined
          ) {
            updateAxleGroup({
              variables: {
                input: {
                  axleGroupId: group.id,
                  weight: 0,
                  sizeAndWeightId,
                  originatorId: userId,
                },
              },
            });
            dispatch({
              type: "UPDATE_AXLE_GROUP",
              payload: { axleGroupId: group.id, value: 0, property: "weight" },
            });
          }
        });
      }
    }
  };

  const handleRemoveAxleGroup = axleGroupId => {
    if (window.confirm("Are you sure you want to remove this axle group?")) {
      try {
        removeAxleGroup({
          variables: {
            input: {
              axleGroupId,
              sizeAndWeightId,
              originatorId: userId,
            },
          },
        });
        dispatch({ type: "REMOVE_AXLE_GROUP", payload: { axleGroupId } });
      } catch (e) {
        console.error("Error removing axle group", e);
      }
    }
  };

  const handleOverUnderUpdates = (value, dispatchAction, payload) => {
    const overUnderName =
      payload?.property === "registeredWeight"
        ? "platformOverUnder"
        : "roadLimitOverUnder";
    const overUnderValue = grossWeight - (+value || 0);
    handleChange(value, dispatchAction, payload);
    handleChange(overUnderValue, "UPDATE_PROPERTY", {
      property: overUnderName,
    });
  };

  const handleSteerAxleChange = (value, dispatchAction, payload) => {
    handleChange(value, dispatchAction, payload);
    handleChange(+value * 600 * 2, "UPDATE_PROPERTY", {
      property: "steerAxleLimit",
    });
  };

  useEffect(() => {
    if (
      Array.isArray(axleGroups) &&
      axleGroups.length === 0 &&
      !!sizeAndWeightId &&
      !locked
    ) {
      const lockState = getAutoCreateLock(sizeAndWeightId);
      if (lockState === "pending" || lockState === "done") return;
      //if there are no axle groups on mount, add one then update the state
      setAutoCreateLock(sizeAndWeightId, "pending");
      addAxleGroup();
    }
  }, [addAxleGroup, axleGroups, sizeAndWeightId, locked]);

  let axleCount = 0;
  return (
    <CollapsingHeader
      id="axle-group-weights-section-collapsing-header"
      nightMode={nightMode}
      title={"Axle Group Weights"}
      defaultOpen={true}
    >
      <div
        id={"platform-allowable-road-container"}
        className={styles["flexRow"]}
      >
        <CheckboxWithLabel
          id={"platform-weigh-checkbox"}
          formStyle={classes.formControl}
          checked={platformWeigh}
          onChange={e =>
            handlePlatformWeighChange(e.target.checked, "UPDATE_PROPERTY", {
              property: "platformWeigh",
            })
          }
          stateName={"platformWeigh"}
          title={"Platform Weigh"}
          disabled={disabled}
          userRoles={userRoles}
          {...props}
        />
        <OverUnderWeightComponent
          missingFields={missingFields}
          weightName={"registeredWeight"}
          handleChange={dispatch}
          handleBlur={handleOverUnderUpdates}
          weightLimit={registeredWeight}
          overUnderWeight={platformOverUnder}
          disabled={disabled}
          label={"Allowable\xa0Weight"}
        />
        <OverUnderWeightComponent
          missingFields={missingFields}
          weightName={"roadLimitWeight"}
          handleChange={dispatch}
          handleBlur={handleOverUnderUpdates}
          weightLimit={roadLimitWeight}
          overUnderWeight={roadLimitOverUnder}
          disabled={disabled}
          label={"Road\xa0Limit\xa0Weight"}
        />
        <NumberField
          id="gross-weight"
          label={"Gross Weight"}
          value={grossWeight}
          lbs={true}
          disabled={true}
          bold={true}
        />
      </div>
      <div id="steer-axle-container" className={styles["flexRow"]}>
        <Required
          required={missingFields.some(
            field => field?.name === "steerAxleTireSize",
          )}
          message={
            missingFields.find(field => field?.name === "steerAxleTireSize")
              ?.error
          }
        >
          <DimensionNumberField
            id="steer-axle-tire-size"
            title="Steer Axle Tire Size"
            totalInches={steerAxleTireSize}
            property="steerAxleTireSize"
            handleChange={handleSteerAxleChange}
            showMetersCalc={false}
            showMillimetersCalc={true}
            hideFeet={true}
            disabled={disabled}
          />
        </Required>
        <TextField
          id="steer-axle-limit"
          name={"steerAxleLimit"}
          inputProps={{
            className: disabled ? classes.disabled : "",
            style: { textAlign: "center" },
          }}
          label="Steer Axle Limit"
          value={(+steerAxleLimit)?.toLocaleString() || ""}
          className={classes.matchMargin}
          disabled={true}
          variant="outlined"
          InputLabelProps={{ shrink: true }}
          InputProps={{
            endAdornment: <InputAdornment position="end">lbs</InputAdornment>,
          }}
        />
      </div>
      <div id="axle-group-header" className={styles["flexRow"]}>
        <button
          type="button"
          onClick={() => !disabled && addAxleGroup()}
          disabled={disabled}
          style={{ background: "none", border: 0, padding: 0 }}
        >
          <img
            id={"add-axle-group-button"}
            src={addButton}
            className={disabled ? classes.disabledPngButton : classes.pngButton}
            alt="Add Axle Group"
          />
        </button>
        <Typography style={{ margin: "0 8px", fontWeight: "bold" }}>
          Axle&nbsp;Groups
        </Typography>
      </div>
      {axleGroups?.map((group) => {
        const currentAxleCount = axleCount;
        axleCount += group.axles.length;
        return (
          <AxleGroupComponent
            platformWeigh={platformWeigh}
            canEdit={props.canEdit}
            key={group?.id}
            group={group}
            dispatch={dispatch}
            userId={userId}
            sizeAndWeightId={sizeAndWeightId}
            removeAxleGroup={handleRemoveAxleGroup}
            userRoles={userRoles}
            mostOverWeightAxleGroup={mostOverWeightAxleGroup}
            steerAxleLimit={+steerAxleTireSize * 600 * 2}
            missingFields={missingFields}
            startingAxleIndex={currentAxleCount}
            disabled={disabled}
            updateGrossWeight={updateGrossWeight}
            nightMode={nightMode}
            locked={locked}
          />
        );
      })}
    </CollapsingHeader>
  );
};

const OverUnderWeightComponent = ({
  missingFields,
  weightName,
  handleBlur,
  handleChange,
  weightLimit,
  overUnderWeight,
  disabled,
  label,
}) => {
  return (
    <div className={styles["flexColumn"]}>
      <Required
        required={missingFields.some(field => field?.name === weightName)}
        message={missingFields.find(field => field?.name === weightName)?.error}
      >
        <NumberField
          id={label}
          label={label}
          value={weightLimit}
          dispatch={handleChange}
          handleBlur={handleBlur}
          action={"UPDATE_PROPERTY"}
          property={weightName}
          disabled={disabled}
          lbs={true}
        />
      </Required>
      <NumberField
        id={label + "-over-under"}
        value={overUnderWeight}
        label={`${overUnderWeight > 0 ? "Over" : "Under"} Limit`}
        lbs={true}
        bold={true}
        disabled={true}
        showColor={true}
      />
    </div>
  );
};
export default AxleGroupWeightsSection;
